
import { defineComponent } from 'vue'
import { createValidations, ICustomValidatorMap } from '@/helpers/commons/validationHelper'
import { iUser, iRole, iAttribute } from './UsersManagement'
import useValidate from '@vuelidate/core'
import { AxiosResponse } from 'axios'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import KnFabButton from '@/components/UI/KnFabButton.vue'
import KnHint from '@/components/UI/KnHint.vue'
import RolesTab from './UserRolesTab/RolesTab.vue'
import DetailFormTab from './UserDetailTab/DetailFormTab.vue'
import KnListBox from '@/components/UI/KnListBox/KnListBox.vue'
import UserAttributesForm from './UserAttributesTab/UserAttributesForm.vue'
import detailFormTabValidationDescriptor from './UserDetailTab/DetailFormTabValidationDescriptor.json'
import usersManagementDescriptor from './UsersManagementDescriptor.json'
import { sameAs } from '@vuelidate/validators'

export default defineComponent({
    name: 'user-management',
    components: { KnListBox, TabView, TabPanel, KnFabButton, KnHint, RolesTab, DetailFormTab, UserAttributesForm },
    data() {
        return {
            v$: useValidate() as any,
            apiUrl: process.env.VUE_APP_RESTFUL_SERVICES_PATH + '2.0/',
            users: [] as iUser[],
            roles: [] as iRole[],
            detailFormTabValidationDescriptor: detailFormTabValidationDescriptor,
            attributes: [],
            userDetailsForm: {} as any,
            dirty: false,
            formInsert: true,
            attributesForm: {},
            tempAttributes: {},
            defaultRole: null,
            hiddenForm: true,
            disableUsername: true,
            loading: false,
            selectedRoles: [] as iRole[],
            usersManagementDescriptor: usersManagementDescriptor
        }
    },
    validations() {
        const customValidators: ICustomValidatorMap = {
            'custom-required': (value) => {
                return !this.formInsert || value
            },
            'password-format': (value) => {
                return value.match(/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-_|#$])[A-Za-z\d\-_|#$]{8,}/)
            },
            'custom-sameAs': sameAs(this.userDetailsForm.password)
        }
        const validationObject = {
            userDetailsForm: createValidations('userDetailsForm', detailFormTabValidationDescriptor.validations.userDetailsForm, customValidators)
        }

        return validationObject
    },
    computed: {
        passwordValidation():boolean {
            return !this.userDetailsForm.password || (this.userDetailsForm.password && !this.v$.userDetailsForm.$invalid)
        }
    },
    async created() {
        await this.loadAllUsers()
        await this.loadAllRoles()
        await this.loadAllAttributes()
    },
    methods: {
        async loadAllUsers() {
            this.loading = true
            await this.$http
                .get(this.apiUrl + 'users')
                .then((response: AxiosResponse<any>) => {
                    this.users = response.data
                })
                .finally(() => (this.loading = false))
        },
        async loadAllRoles() {
            this.loading = true
            await this.$http
                .get(this.apiUrl + 'roles')
                .then((response: AxiosResponse<any>) => {
                    this.roles = response.data
                })
                .finally(() => (this.loading = false))
        },
        async loadAllAttributes() {
            this.loading = true
            await this.$http
                .get(this.apiUrl + 'attributes')
                .then((response: AxiosResponse<any>) => {
                    this.attributes = response.data
                })
                .finally(() => (this.loading = false))
        },
        setDefaultRoleValue(defaultRole: any) {
            this.defaultRole = defaultRole
            this.dirty = true
        },
        setSelectedRoles(roles: iRole[]) {
            this.selectedRoles = roles
            this.dirty = true
        },
        async showForm() {
            this.tempAttributes = {}
            this.attributesForm = {}
            this.disableUsername = false
            this.hiddenForm = false
            this.selectedRoles = []
            this.userDetailsForm.id = null
            this.userDetailsForm.userId = ''
            this.userDetailsForm.fullName = ''
            this.userDetailsForm.failedLoginAttempts = 0
            this.userDetailsForm.sbiExtUserRoleses = []
            this.userDetailsForm.sbiUserAttributeses = {}

            this.formInsert = true
            this.dirty = false
            this.v$.$reset()

            this.populateForms(this.userDetailsForm)
        },
        formatUserObject() {
            const userToSave = { ...this.userDetailsForm }
            delete userToSave.passwordConfirm
            userToSave['defaultRoleId'] = this.defaultRole
            for (var key in this.attributesForm) {
                for (var key2 in this.attributesForm[key]) {
                    this.attributesForm[key][key2] === '' ? delete this.attributesForm[key] : ''
                }
            }
            userToSave['sbiUserAttributeses'] = { ...this.attributesForm }
            userToSave['sbiExtUserRoleses'] = this.selectedRoles ? [...this.selectedRoles.map((selRole) => selRole.id)] : []
            return userToSave
        },
        onFormDirty() {
            this.dirty = true
        },
        saveOrUpdateUser(user: iUser) {
            const endpointPath = `${process.env.VUE_APP_RESTFUL_SERVICES_PATH}2.0/users`
            return this.userDetailsForm.id ? this.$http.put<any>(`${endpointPath}/${user.id}`, user) : this.$http.post<any>(endpointPath, user)
        },
        async saveUser() {
            this.loading = true
            if (!this.selectedRoles || this.selectedRoles.length == 0) {
                this.$store.commit('setError', {
                    title: this.userDetailsForm.id ? this.$t('common.toast.updateTitle') : this.$t('managers.usersManagement.info.createTitle'),
                    msg: this.$t('managers.usersManagement.error.noRolesSelected')
                })
                this.loading = false
            } else {
                const userToSave = this.formatUserObject()
                this.saveOrUpdateUser(userToSave)
                    .then((response: AxiosResponse<any>) => {
                        this.afterSaveOrUpdate(response)
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        },
        async afterSaveOrUpdate(response: AxiosResponse<any>) {
            this.dirty = false
            await this.loadAllUsers()
            this.formInsert = false
            const id: number | null = response.data
            const selectedUser = this.users.find((user) => {
                return user.id === id
            })
            if (selectedUser) {
                this.onUserSelect(null, selectedUser)
            }
            this.$store.commit('setInfo', {
                title: this.userDetailsForm.id ? this.$t('common.toast.updateTitle') : this.$t('managers.usersManagement.info.createTitle'),
                msg: this.userDetailsForm.id ? this.$t('common.toast.updateSuccess') : this.$t('managers.usersManagement.info.createMessage')
            })
        },
        onUserDelete(event: any) {
            this.$confirm.require({
                message: this.$t('common.toast.deleteMessage'),
                header: this.$t('common.uppercaseDelete'),
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    this.loading = true
                    this.$http
                        .delete(`${process.env.VUE_APP_RESTFUL_SERVICES_PATH}2.0/users/${event.item.id}`)
                        .then(() => {
                            this.loadAllUsers()
                            this.$store.commit('setInfo', {
                                title: this.$t('managers.usersManagement.info.deleteTitle'),
                                msg: this.$t('managers.usersManagement.info.deleteMessage')
                            })
                        })
                        .catch((error) => {
                            this.$store.commit('setError', {
                                title: error.title,
                                msg: error.msg
                            })
                        })
                        .finally(() => {
                            this.hiddenForm = true
                            this.loading = false
                        })
                }
            })
        },
        async unlockUser() {
            this.userDetailsForm.failedLoginAttempts = 0
            await this.saveUser()
        },
        async onUserSelect(event, userSelected: any) {
            const user = userSelected || event.item
            this.formInsert = false
            if (this.dirty) {
                this.$confirm.require({
                    message: this.$t('common.toast.unsavedChangesMessage'),
                    header: this.$t('common.toast.unsavedChangesHeader'),
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        this.populateForms(user)
                        this.dirty = false
                    },
                    reject: () => {}
                })
            } else {
                this.populateForms(user)
            }
        },
        populateForms(userObj: any) {
            this.dirty = false
            this.attributesForm = {}
            this.hiddenForm = false
            this.disableUsername = true
            this.defaultRole = userObj.defaultRoleId
            this.selectedRoles = this.getSelectedUserRoles(userObj.sbiExtUserRoleses)
            this.userDetailsForm = { ...userObj }
            this.populateAttributesForm(userObj.sbiUserAttributeses)
        },
        populateAttributesForm(userAttributeValues: any) {
            const tmp = {}
            this.attributes.forEach((attribute: iAttribute) => {
                let obj = {}
                obj[attribute.attributeName] = userAttributeValues && userAttributeValues[attribute.attributeId] ? userAttributeValues[attribute.attributeId][attribute.attributeName] : null
                tmp[attribute.attributeId] = obj
            })
            this.attributesForm = { ...tmp }
        },
        getSelectedUserRoles(userRoles: number[]) {
            return this.roles ? [...this.roles.filter((role) => userRoles && userRoles.find((userRoleId) => role.id === userRoleId))] : []
        },
        closeForm() {
            if (this.dirty) {
                this.$confirm.require({
                    message: this.$t('common.toast.unsavedChangesMessage'),
                    header: this.$t('common.toast.unsavedChangesHeader'),
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        this.hiddenForm = true
                        this.dirty = false
                    },
                    reject: () => {}
                })
            } else {
                this.hiddenForm = true
            }
        },
        onDataChange() {
            this.dirty = true
        }
    }
})
